import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { deleteUsers } from '../services/users';

/**
 * User deletion dialog component
 */
const UserDeleteDialog = ({ open, onClose, user, onUserDeleted }) => {
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirmationChange = (e) => {
    setConfirmation(e.target.value);
    if (error) setError('');
  };

  const handleClose = () => {
    setConfirmation('');
    setError('');
    onClose();
  };

  const handleDelete = async () => {
    const userName = user.profile?.userName || 'N/A';
    
    if (confirmation !== userName) {
      setError(`To confirm, enter username: ${userName}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await deleteUsers([user.id]);
      
      // Call the callback with deletion information
      if (typeof onUserDeleted === 'function') {
        onUserDeleted(user.id);
      }
      
      // Close the dialog
      handleClose();
    } catch (err) {
      setError(err.message || 'An error occurred while deleting the user');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  const userName = user.profile?.userName || 'N/A';
  const userEmail = user.profile?.email || 'N/A';
  const userId = user.id || 'Unknown';

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
        Delete User
      </DialogTitle>
      
      <DialogContent dividers>
        <DialogContentText component="div">
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold' }}>
              WARNING! This action cannot be undone!
            </Typography>
            <Typography variant="body2">
              Deletion will permanently remove all user data, including profile,
              photos, messages, and other related data.
            </Typography>
          </Alert>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              User Information:
            </Typography>
            <Typography variant="body1">
              Name: <strong>{user.profile?.name || 'Not specified'}</strong>
            </Typography>
            <Typography variant="body1">
              Username: <strong>{userName}</strong>
            </Typography>
            <Typography variant="body1">
              Email: <strong>{userEmail}</strong>
            </Typography>
            <Typography variant="body1">
              ID: <strong>{userId}</strong>
            </Typography>
          </Box>
          
          <Typography sx={{ mb: 1 }}>
            To confirm deletion, enter username: <strong>{userName}</strong>
          </Typography>
          
          <TextField
            fullWidth
            value={confirmation}
            onChange={handleConfirmationChange}
            label="Confirmation"
            placeholder={`Type "${userName}" to confirm`}
            variant="outlined"
            disabled={loading}
            error={!!error}
            helperText={error}
            autoFocus
          />
        </DialogContentText>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={loading || confirmation !== userName}
          onClick={handleDelete}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          {loading ? 'Deleting...' : 'Delete User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDeleteDialog;